import React from "react";
import styled from "styled-components/macro";
import { useCoachPicture } from "../../../hooks/useCoachPicture";

const CardWrapper = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.primaryBackground};
  padding: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.boxShadow.md};
`;

const SectionLabel = styled.p`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.body};
  margin: 0 0 12px;
`;

const CoachInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const AvatarWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.altBackground};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarInitials = styled.span`
  font-weight: 700;
  font-size: 17px;
  color: ${({ theme }) => theme.colors.heading};
  text-transform: uppercase;
  line-height: 1;
`;

const CoachMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CoachName = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${({ theme }) => theme.colors.heading};
`;

const CoachBio = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.body};
`;

const NoCoachWrapper = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const NoCoachText = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.body};
`;

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("");
}

/**
 * GPDS-889 — Coach profile module (Tier 1)
 * Displays a card with circular avatar (initial fallback), coach name, label, and bio.
 * Renders an empty state when no coach is assigned.
 */
export default function CoachProfileModule({ coach }) {
  const { objectUrl, imgError, setImgError, loading } = useCoachPicture(
    coach?.email
  );

  const showFallback = loading || imgError || !objectUrl;

  return (
    <CardWrapper>
      <SectionLabel>Your Coach</SectionLabel>
      {coach ? (
        <CoachInfo>
          <AvatarWrapper aria-hidden="true">
            {showFallback ? (
              <AvatarInitials>{getInitials(coach.name)}</AvatarInitials>
            ) : (
              <AvatarImg
                src={objectUrl}
                alt={coach.name}
                onError={() => setImgError(true)}
              />
            )}
          </AvatarWrapper>
          <CoachMeta>
            <CoachName>{coach.name}</CoachName>
            <CoachBio>
              Your coach and digital tools work together to support your
              progress.
            </CoachBio>
          </CoachMeta>
        </CoachInfo>
      ) : (
        <NoCoachWrapper>
          <NoCoachText>No coach assigned yet.</NoCoachText>
          <NoCoachText>
            We&apos;re matching you with the right coach.
          </NoCoachText>
        </NoCoachWrapper>
      )}
    </CardWrapper>
  );
}
